import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, TextField, Typography, IconButton } from "@mui/material";
import { styled } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { clearForgotData, forgotPassword } from "../../redux/authSlice";
import Path from "../../router/LocalRoutes";
import Topnav from "../../components/Topnav";
import ArrowBackIcon from "../../assets/images/Arrow Left.svg"; // Import back arrow icon
import useValidation from "../../hooks/useValidation";
import Background from "../../components/Background";
import Copyrighttext from "../../components/Copyrighttext";
import "./Signin.css";
import "./ForgotPassword.css";

const RoundedTextField = styled(TextField)({
  borderRadius: "20px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    backgroundColor: "#F1F7FA",
  },
  "& .MuiInputBase-input": {
    padding: "10px",
    color: "#2A4876", // Set the input value color to green
    "&::placeholder": {
      color: "#151516", // Set the placeholder text color to red
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
});

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [formValues, setFormValues] = useState({
    email: "",
  });
  const { errors, validateInputs } = useValidation({
    email: formValues.email,
  });

  useEffect(() => {
    if (auth.forgotStatus === "failed") {
      dispatch(clearForgotData());
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });

    if (auth.forgotStatus === "failed") {
      dispatch(clearForgotData());
    }
  };

  const handleForgetPassword = async () => {
    if (!validateInputs()) {
      return;
    }
    dispatch(forgotPassword(formValues))
      .then((response) => {
        if (response?.payload?.status) {
          navigate(Path.VerifyCode, {
            state: { res: response.payload.data, email: formValues.email },
          });
        }
      })
      .catch((error) => {
        console.log("err", error);
      });
  };

  // Check if the email input is not empty
  const disable =
    auth.forgotStatus === "loading" || formValues.email.trim() === "";

  return (
    <Background>
      <Topnav />
      <IconButton
        onClick={() => navigate(-1)}
        sx={{
          display: { xs: "flex", lg: "none" }, // Show on xs, sm, md, and hide on lg and up
          alignItems: "center !important",
          position: "relative",
          bgcolor: "#2A4876",
          color: "white",
          borderRadius: "20px", // Make it rounded
          padding: "8px 16px !important", // Adjust padding to get the pill shape
          "&:hover": { bgcolor: "#1F3553" },
          marginX: "20px",
          marginTop: "10px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", marginRight: "8px" }}>
          <img src={ArrowBackIcon} alt="Back" />
        </Box>
        <Typography
          sx={{ fontWeight: "bold", fontSize: "14px", fontFamily: "PTSans" }}
        >
          Back
        </Typography>
      </IconButton>
      {/* Back Button in large devices */}
      <IconButton
        onClick={() => navigate(-1)}
        sx={{
          display: { xs: "none", sm: "none", md: "none", lg: "flex" }, // Hide on xs, sm, md, and show on lg and up
          alignItems: "center !important",
          position: "absolute",
          left: "15vw",
          bgcolor: "#2A4876",
          color: "white",
          borderRadius: "20px", // Make it rounded
          padding: "8px 16px !important", // Adjust padding to get the pill shape
          "&:hover": { bgcolor: "#1F3553" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", marginRight: "8px" }}>
          <img src={ArrowBackIcon} alt="Back" />
        </Box>
        <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>Back</Typography>
      </IconButton>

      <Box mt={6} px={6} className="selection-box">
        <Typography
          className="text-primary"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "18px",
              sm: "20px",
              md: "24px",
              lg: "28px",
              xl: "32px",
            },
            lineHeight: {
              xs: "28px",
              sm: "30px",
              md: "24px",
              lg: "38px",
              xl: "42px",
            },
          }}
        >
          Forgot Password?
        </Typography>
        <Typography
          variant="body1"
          className="body"
          sx={{
            fontWeight: 400,
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "14px",
              lg: "16px",
            },
            color: "#152134",
          }}
        >
          Enter the email address associated with your
          <br /> account and we will send you a security code.
        </Typography>
        <Box sx={{ marginTop: "20px" }}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: {
                xs: "12px",
                sm: "13px",
                md: "14px",
              },
              color: "#152134",
              textAlign: "left",
              marginBottom: "5px",
            }}
          >
            Your Email Address
          </Typography>
          <RoundedTextField
            name="email"
            variant="outlined"
            placeholder="Enter here"
            fullWidth
            value={formValues.email}
            error={errors.email}
            helperText={<span className="error-msg">{errors.email}</span>}
            onChange={(e) => handleChange(e)}
            slotProps={{
              input: {
                "aria-label": "email input",
              },
            }}
          />
        </Box>
        {auth.forgotStatus === "failed" && (
          <Typography sx={{ color: "red", marginBottom: "15px" }}>
            {auth.forgotError}
          </Typography>
        )}
        <Button
          variant="contained"
          fullWidth
          className="Send-button"
          onClick={handleForgetPassword}
          sx={{
            backgroundColor: !disable ? "#2A4876" : "#AAB6C8", // Change color conditionally
            color: "#fff",
            marginTop: "10px",
            height: {
              xs: "40px",
              sm: "45px",
              md: "45px",
              lg: "50px",
              xl: "60px",
            },
            borderRadius: "100px",
            textTransform: "capitalize",
          }}
        >
          Send
        </Button>
      </Box>
      <Box mt={2} sx={{ display: { xs: "block", sm: "none" } }}>
        <Copyrighttext />
      </Box>
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
        }}
      >
        <Copyrighttext />
      </Box>
    </Background>
  );
};

export default ForgotPassword;
